// todo: can we have a link to case study project? like a github repo or something? maybe in the header or footer? or maybe a "View Project" button ? or what do u think about that?
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ArrowUpRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";
import { supabase } from "@/lib/supabase";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudyPage() {
  const { slug } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      /*
      try {
        const res = await axios.get(`${API_URL}/api/case-studies/${slug}`);
        setCaseStudy(res.data);
      } catch (error) {
        console.error("Error fetching case study:", error);
        setError("Case study not found");
      } finally {
        setLoading(false);
      }
        */

      try {
        const { data, error } = await supabase
          .schema('raindas_technologies_ltd')
          .from('case_study')
          .select()
          .eq('slug', slug)
          .single();

        if (error) throw error;
        setCaseStudy(data);
      } catch (error) {
        console.error("Error fetching case study:", error);
        setError("Case study not found");
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24" data-testid="case-study-loading">
        <div className="py-24 md:py-32">
          <div className="max-w-4xl mx-auto px-6">
            <Skeleton className="h-6 w-32 bg-white/10 mb-8" />
            <Skeleton className="h-16 w-full bg-white/10 mb-6" />
            <Skeleton className="h-8 w-2/3 bg-white/10 mb-12" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full bg-white/10" />
              <Skeleton className="h-4 w-full bg-white/10" />
              <Skeleton className="h-4 w-3/4 bg-white/10" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-screen pt-24" data-testid="case-study-error">
        <div className="py-24 md:py-32 text-center">
          <div className="max-w-3xl mx-auto px-6">
            <h1 className="text-4xl font-heading font-bold text-white mb-6">Case Study Not Found</h1>
            <p className="text-slate-400 text-lg mb-10">The case study you're looking for doesn't exist.</p>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-mint hover:text-coral transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Case Studies
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="case-study-page">
      <article className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back Link */}
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-electric transition-colors mb-12"
            data-testid="case-study-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-wider">Back to Case Studies</span>
          </Link>

          {/* Header */}
          <header className="mb-16">
            <div className="flex items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <span className="text-xs font-mono uppercase tracking-wider text-slate-600">
                  {caseStudy.industry}
                </span>
                {caseStudy.featured && (
                  <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-mint/10 text-mint border border-mint/20">
                    Featured
                  </span>
                )}
              </div>

              {caseStudy.project_url && (
                <a
                  href={caseStudy.project_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-4 py-2 border border-electric/40 text-electric hover:border-electric hover:bg-electric/10 transition-all group"
                >
                  View Project
                  <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              )}
            </div>

            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white tracking-tight leading-[1.1]">
              {caseStudy.title}
            </h1>
            <p className="mt-6 text-slate-500">
              Client: {caseStudy.client}
            </p>
          </header>

          {/* Challenge */}
          <section className="mb-16" data-testid="case-study-challenge">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
              The Challenge
            </p>
            <p className="text-slate-300 text-xl leading-relaxed">
              {caseStudy.challenge}
            </p>
          </section>

          {/* Solution */}
          <section className="mb-16" data-testid="case-study-solution">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
              Our Solution
            </p>
            <p className="text-slate-300 text-xl leading-relaxed">
              {caseStudy.solution}
            </p>
          </section>

          {/* Results */}
          <section className="mb-16" data-testid="case-study-results">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-electric mb-6">
              Results
            </p>
            <div className="glass-card p-10">
              <ul className="space-y-6">
                {caseStudy.results.map((result, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <Check className="w-6 h-6 text-mint flex-shrink-0 mt-0.5" />
                    <span className="text-slate-200 text-lg">{result}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Technologies */}
          <section className="mb-16" data-testid="case-study-technologies">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-electric mb-6">
              Technologies Used
            </p>
            <div className="flex flex-wrap gap-3">
              {caseStudy.technologies.map((tech) => (
                <span
                  key={tech}
                  className="px-4 py-2 bg-white/5 border border-white/10 text-slate-300 text-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
          </section>

          {/* Footer CTA */}
          <footer className="mt-20 pt-10 border-t border-white/5">
            <div className="glass-card p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-mint/10 to-transparent" />
              <h3 className="text-2xl font-heading font-bold text-white mb-4 relative z-10">
                Have a similar challenge?
              </h3>
              <p className="text-slate-400 mb-8 relative z-10">
                Contact us to discuss how we can help you achieve similar results.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-mint text-black px-6 py-4 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors relative z-10 group"
                data-testid="case-study-contact-btn"
              >
                Get in Touch
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </footer>
        </div>
      </article >
    </div >
  );
}
